/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useMemo,
  useRef,
  Component,
  Fragment,
} from 'react';
import isEqual from '@wry/equality';
import type { Libraries } from '@react-google-maps/api';
import {
  Autocomplete,
  DrawingManagerF,
  GoogleMap,
  PolygonF,
  OverlayView,
  OverlayViewF,
  useJsApiLoader,
} from '@react-google-maps/api';
import { Text, TextInput } from '@mantine/core';
import centerOfMass from '@turf/center-of-mass';
import booleanOverlap from '@turf/boolean-overlap';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import pointOnFeature from '@turf/point-on-feature';
import type { Feature, Polygon as TurfPolygon, Position } from '@turf/helpers';
import { point, polygon as turfPolygon } from '@turf/helpers';
import type {
  FieldValues,
  UseControllerProps,
  UseFormClearErrors,
  UseFormSetError,
} from 'react-hook-form';
import { useController } from 'react-hook-form';

import type {
  Latlng,
  Maybe,
  Port_AdminFragment,
  Region,
  Scalars,
} from '../graphql';
import { useAllRegionsQuery } from '../graphql';
import type { PortCallEvent } from './windy/leafletMap';
import LeafletMap from './windy/leafletMap';
import { isDefined } from '../lib/util';
import LoadingSpinner from './LoadingSpinner';
import { Box } from './ui';

export const googleMapsApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS || '';
export const googleMapsClientId = process.env.GOOGLE_CLIENT_ID || '';
export const libraries: Libraries = ['drawing', 'places'];

/**
 * Custom hook to load Google Maps API
 */
export function useSharedGoogleMapsLoader() {
  return useJsApiLoader({
    id: 'google-maps-script',
    googleMapsApiKey,
    libraries,
    googleMapsClientId,
  });
}

const mapContainerStyle = {
  height: '50vh',
  width: '100%',
};

const center = {
  lat: 56.226081072213745,
  lng: 2.3799562454223637,
};

type AutocompleteType = google.maps.places.Autocomplete;

type NotNullCoordinate = {
  lat: Scalars['Float']['output'];
  lng: Scalars['Float']['output'];
};
type NotNullRegion = Pick<Region, 'id' | 'name'> & {
  polygon: NotNullCoordinate[];
};
type Regions = NotNullRegion[];
export type RegionPolygons = Feature<TurfPolygon, { name: string }>[];

interface PlaceSuggestProps {
  onChange?: ({ lat, lng }: { lat: number; lng: number }) => void;
  point: [number, number];
  events?: PortCallEvent[];
}

function polygonToLatLngLiteralArray(
  polygon: google.maps.Polygon,
): google.maps.LatLngLiteral[] {
  return polygon
    .getPath()
    .getArray()
    .map((latLng) => {
      return { lat: latLng.lat(), lng: latLng.lng() };
    });
}

function overlap(
  polygon: Feature<TurfPolygon>,
  polygons: Feature<TurfPolygon, { name: string }>[],
): Feature<TurfPolygon, { name: string }>[] | undefined {
  if (polygon && polygons) {
    const overlaps = polygons.filter((p) => booleanOverlap(polygon, p));
    if (overlaps.length) return overlaps;
  }
  return undefined;
}

export function notNullPolygon(
  polygon: Array<Maybe<Latlng>>,
): polygon is NotNullCoordinate[] {
  return !polygon.some((c) => c === null || c.lat === null || c.lng === null);
}

export function useAllRegions(skip = false) {
  const {
    data,
    loading: regionsLoading,
    error: regionsError,
  } = useAllRegionsQuery({
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const nodes = useMemo(
    () => data?.allRegions?.nodes?.filter(isDefined) ?? [],
    [data?.allRegions?.nodes],
  );

  const totalCount = useMemo(() => nodes.length ?? 0, [nodes]);

  return { nodes, totalCount, regionsLoading, regionsError };
}

const useOverlapValidator = (
  id: number | undefined,
  validation: boolean,
): {
  points: Position[] | undefined;
  regions: Regions | undefined;
  validate: (value: null | google.maps.LatLngLiteral[]) => string | null;
} => {
  const { nodes, regionsError, regionsLoading } = useAllRegions();

  const regions = useMemo<Regions | undefined>(
    () =>
      validation
        ? nodes.filter(
            (r): r is NotNullRegion =>
              r != null &&
              r.id !== id &&
              r.polygon != null &&
              notNullPolygon(r.polygon),
          )
        : [],
    [validation, nodes, id],
  );
  const polygons = useMemo<
    Feature<TurfPolygon, { name: string }>[] | undefined
  >(
    () =>
      regions?.map((region) => {
        const linearRing = region.polygon.map(({ lat, lng }) => [lat, lng]);
        linearRing.push(linearRing[0]);
        return turfPolygon([linearRing], { name: region.name });
      }),
    [regions],
  );
  const points = useMemo<Position[] | undefined>(
    () =>
      polygons?.map((polygon) => {
        const turfCenter = centerOfMass(polygon).geometry.coordinates;
        const isPointInPolygon = booleanPointInPolygon(turfCenter, polygon);
        return isPointInPolygon
          ? turfCenter
          : pointOnFeature(polygon).geometry.coordinates;
      }),
    [polygons],
  );

  const validate = useCallback(
    (value: null | google.maps.LatLngLiteral[]): string | null => {
      if (regionsLoading || !value) {
        return null;
      }
      if (regionsError) {
        return 'error loading region data';
      }
      if (!polygons) {
        return null;
      }

      if (value.length < 3) {
        return 'Polygon needs at least three vertices';
      }

      const linearRing = value.map(({ lat, lng }) => [lat, lng]);
      linearRing.push(linearRing[0]);
      if (linearRing.length < 4) {
        return null;
      }
      const polygon = turfPolygon([linearRing]);
      const overlaps = overlap(polygon, polygons);
      if (overlaps) {
        return (
          'Overlaps with: ' +
          overlaps.map((polygon) => polygon.properties.name).join(', ')
        );
      }
      return null;
    },
    [polygons, regionsLoading, regionsError],
  );

  return {
    points,
    regions,
    validate,
  };
};

function attachContextMenu(polygon: google.maps.Polygon, onEdit: () => void) {
  const map = polygon.getMap();
  if (!map) return;
  google.maps.event.addListener(
    polygon,
    'contextmenu',
    (e: google.maps.PolyMouseEvent) => {
      // Check if click was on a vertex control point
      if (e.vertex == undefined) {
        return;
      }

      class DeleteMenu extends google.maps.OverlayView {
        private readonly div_: HTMLDivElement;
        private divListener_?: google.maps.MapsEventListener;

        constructor() {
          super();
          this.div_ = document.createElement('div');
          this.div_.className = 'delete-menu';
          this.div_.innerHTML = 'Delete';

          google.maps.event.addDomListener(this.div_, 'click', () => {
            this.removeVertex();
          });
        }

        onAdd() {
          const map = this.getMap() as google.maps.Map;

          const panes = this.getPanes();

          if (!panes) {
            return;
          }

          panes.floatPane.appendChild(this.div_);

          // mousedown anywhere on the map except on the menu div will close the
          // menu.
          this.divListener_ = google.maps.event.addDomListener(
            map.getDiv(),
            'mousedown',
            (e: Event) => {
              if (e.target != deleteMenu.div_) {
                this.close();
              }
            },
            true,
          );
        }

        onRemove() {
          if (this.divListener_) {
            google.maps.event.removeListener(this.divListener_);
          }

          (this.div_.parentNode as HTMLElement).removeChild(this.div_);

          // clean up
          this.set('position', null);
          this.set('path', null);
          this.set('vertex', null);
        }

        close() {
          this.setMap(null);
        }

        draw() {
          const position = this.get('position');
          const projection = this.getProjection();

          if (!position || !projection) {
            return;
          }

          const point = projection.fromLatLngToDivPixel(position);

          if (!point) {
            return;
          }

          this.div_.style.top = point.y + 'px';
          this.div_.style.left = point.x + 'px';
        }

        /**
         * Opens the menu at a vertex of a given path.
         */
        open(
          map: google.maps.Map,
          path: google.maps.MVCArray<google.maps.LatLng>,
          vertex: number,
        ) {
          this.set('position', path.getAt(vertex));
          this.set('path', path);
          this.set('vertex', vertex);
          this.setMap(map);
          this.draw();
        }

        /**
         * Deletes the vertex from the path.
         */
        removeVertex() {
          const path = this.get('path');
          const vertex = this.get('vertex');

          if (!path || vertex == undefined) {
            this.close();
            return;
          }

          path.removeAt(vertex);
          this.close();
          onEdit();
        }
      }

      const deleteMenu = new DeleteMenu();
      deleteMenu.open(map, polygon.getPath(), e.vertex);
    },
  );
}

export function useRegionPolygons(): RegionPolygons | undefined {
  const { nodes } = useAllRegions();

  const regions = useMemo<Regions | undefined>(
    () =>
      nodes.filter(
        (r): r is NotNullRegion =>
          r != null && r.polygon != null && notNullPolygon(r.polygon),
      ),
    [nodes],
  );

  return useMemo<RegionPolygons | undefined>(
    () =>
      regions?.map((region) => {
        const linearRing = region.polygon.map(({ lat, lng }) => [lat, lng]);
        linearRing.push(linearRing[0]);
        return turfPolygon([linearRing], { name: region.name });
      }),
    [regions],
  );
}
export function getRegionForLocation(
  regionPolygons: RegionPolygons | undefined,
  record: Port_AdminFragment,
): string | undefined {
  if (!regionPolygons) {
    return 'Loading...';
  }

  if (!record.latitude || !record.longitude) {
    return;
  }

  const pt = point([Number(record.latitude), Number(record.longitude)]);
  const region = regionPolygons.find((polygon) =>
    booleanPointInPolygon(pt, polygon),
  );

  return region?.properties.name;
}

export function GooglePolygonInput<T extends FieldValues>(
  props: UseControllerProps<T> & {
    id: number | undefined;
    setError: UseFormSetError<T>;
    clearErrors: UseFormClearErrors<T>;
    validation: boolean;
    editable: boolean;
    color?: string;
  },
) {
  const { id, setError, clearErrors, validation, editable, color, ...rest } =
    props;
  const {
    field: { value, onChange },
  } = useController(rest);

  const overlapValidate = useOverlapValidator(id, validation);

  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);

  const onChangeHandler = useCallback(
    (value: google.maps.LatLngLiteral[] | null) => {
      const error = overlapValidate.validate(value);
      if (error) {
        setError(props.name, { type: 'custom', message: error });
      } else {
        clearErrors(props.name);
      }

      onChange(value);
    },
    [setError, clearErrors, onChange, overlapValidate, props.name],
  );

  const onPolygonComplete = useCallback(
    (polygon: google.maps.Polygon) => {
      const next = polygonToLatLngLiteralArray(polygon);
      polygon.setMap(null);
      onChangeHandler(next);
    },
    [onChangeHandler],
  );

  const onEdit = useCallback(() => {
    if (polygonRef.current) {
      const next = polygonToLatLngLiteralArray(polygonRef.current);
      if (isEqual(next, value)) {
        return;
      }
      if (next.length < 2) {
        onChangeHandler(null);
      } else {
        onChangeHandler(next);
      }
    }
  }, [onChangeHandler, value]);

  const onLoad = useCallback(
    (polygon: google.maps.Polygon) => {
      polygonRef.current = polygon;
      const path = polygon.getPath();
      const listeners = listenersRef.current;
      listeners.forEach((lis) => lis.remove());
      listeners.length = 0;
      listeners.push(
        path.addListener('set_at', onEdit),
        path.addListener('insert_at', onEdit),
        path.addListener('remove_at', onEdit),
      );
      attachContextMenu(polygon, onEdit);
    },
    [onEdit],
  );

  const onUnmount = useCallback(() => {
    listenersRef.current.forEach((lis) => lis.remove());
    polygonRef.current = null;
  }, []);

  const { isLoaded, loadError } = useSharedGoogleMapsLoader();

  return loadError ? (
    <Box display="flex" alignItems="center" justifyContent="center" p={4}>
      <Text color="red">Error loading Google Maps: {loadError.message}</Text>
    </Box>
  ) : isLoaded ? (
    <>
      <GoogleMap mapContainerStyle={mapContainerStyle} zoom={4} center={center}>
        {overlapValidate.regions?.map((region, i) => {
          const point = overlapValidate.points?.[i];

          if (!point) return null;

          return (
            <Fragment key={i}>
              <PolygonF path={region.polygon} />
              <OverlayViewF
                position={{ lat: point[0], lng: point[1] }}
                mapPaneName={OverlayView.MARKER_LAYER}
              >
                <div
                  style={{
                    padding: 3,
                    marginTop: '-50%',
                    marginRight: '50%',
                    marginLeft: '-50%',
                    backgroundColor: 'white',
                  }}
                >
                  {region.name}
                </div>
              </OverlayViewF>
            </Fragment>
          );
        })}
        {value ? (
          <PolygonF
            path={value}
            onLoad={onLoad}
            onMouseUp={onEdit}
            editable={editable}
            onUnmount={onUnmount}
            options={{
              fillColor: color,
            }}
          />
        ) : (
          <DrawingManagerF
            onPolygonComplete={onPolygonComplete}
            drawingMode={
              window.google?.maps.drawing.OverlayType.POLYGON ?? null
            }
            options={{
              drawingControlOptions: {
                position: window.google?.maps.ControlPosition.TOP_CENTER,
                drawingModes: [window.google?.maps.drawing.OverlayType.POLYGON],
              },
              polygonOptions: {
                fillColor: color,
              },
            }}
          />
        )}
      </GoogleMap>
    </>
  ) : (
    <Box display="flex" alignItems="center" justifyContent="center" p={4}>
      <LoadingSpinner />
    </Box>
  );
}

class PlaceSuggestInner extends Component<PlaceSuggestProps> {
  state = { point: this.props.point, events: this.props.events };
  autocomplete: AutocompleteType;

  onLoad = (autocomplete: AutocompleteType) => {
    this.autocomplete = autocomplete;
  };
  onPlaceChanged = () => {
    if (this.autocomplete !== null && this.props.onChange) {
      const place = JSON.parse(JSON.stringify(this.autocomplete.getPlace()));
      const {
        address_components,
        geometry: {
          location: { lat, lng },
          viewport: { south, west, north, east },
        },
        utc_offset,
        utc_offset_minutes,
        name,
      } = place;
      const locality = address_components.find(
        (a: any) => a.types[0] === 'locality',
      );
      const { long_name = name } = locality || {};
      const country = address_components.find(
        (a: any) => a.types[0] === 'country',
      );
      const { short_name: code } = country || {};
      const placeData = {
        name: long_name,
        code,
        lat,
        lng,
        south,
        west,
        north,
        east,
        utc_offset,
        utc_offset_minutes,
      };

      this.setState({ point: [placeData.lat, placeData.lng] });
      this.props.onChange(placeData);
    } else {
      console.log('Autocomplete is not loaded yet!');
    }
  };

  render() {
    const {
      point: [lat, lng],
    } = this.state;

    const point = lat && lng ? this.state.point : undefined;

    const events = this.state.events || [];

    return (
      <React.Fragment>
        <Autocomplete onLoad={this.onLoad} onPlaceChanged={this.onPlaceChanged}>
          <TextInput placeholder="Search place names" />
        </Autocomplete>
        <LeafletMap
          point={point}
          onChange={(value: { lng: number; lat: number }) => {
            if (this.props.onChange) {
              this.props.onChange(value);
              this.setState({ point: [value.lat, value.lng] });
            }
          }}
          events={events}
        />
      </React.Fragment>
    );
  }
}

function PlaceSuggest(props: PlaceSuggestProps) {
  const { isLoaded, loadError } = useSharedGoogleMapsLoader();

  if (loadError) {
    return (
      <Box display="flex" alignItems="center" p={2}>
        <Text color="red">Error loading Google Maps: {loadError.message}</Text>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box display="flex" alignItems="center" p={2}>
        <LoadingSpinner size={16} />
      </Box>
    );
  }

  return <PlaceSuggestInner {...props} />;
}

export default PlaceSuggest;
